import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  EMPTY_PROGRESS,
  WATCHED_PERCENT,
  buildCategoryTree,
  buildGenreShelf,
  buildVodShelf,
  genrePage,
  isLocked,
  nextEpisodeOf,
  rootsAlphabetically,
  episodeAfter,
  episodeProgressKey,
  recordEpisodeProgress,
  recordMovieProgress,
  similarTo,
  thrownDetail,
  withSeasons,
  type Credentials,
  type EpisodeInfo,
  type ErrorCode,
  type GenreSort,
  type MovieDetail,
  type SeriesDetail,
  type VodItem,
  type VodKind,
  type WatchProgress,
} from '@player/domain';
import {
  PinPrompt,
  VodDetailScreen,
  VodGenreScreen,
  VodGenresScreen,
  VodPlayerScreen,
  VodScreen,
  useTranslator,
  rowTitle,
  PhoneVodScreen,
  PhoneGenresScreen,
  PhoneVodDetailScreen,
  PhoneGenrePageScreen,
} from '@player/ui';
import type { ChannelCategory } from '@player/ports';
import { usePhoneLayout } from './usePhoneLayout.js';
import type { Composition } from './composition.js';
import type { CatalogCache } from './catalogCache.js';

/**
 * Quel che sta **intorno** a Film e Serie TV: i dati, l'archivio e i tre passaggi di schermata.
 *
 * ## Perché è un componente e non tre rami dentro `LiveHost`
 *
 * Per la stessa ragione per cui esiste `LiveHost`: gli hook non stanno dentro un ramo. E perché il
 * catalogo on demand è un carico che non va pagato da chi non apre quella sezione — 6824 film su
 * un'installazione vera. Montando questo componente solo quando si entra, chi guarda soltanto la
 * televisione non scarica mai il listino dei film.
 *
 * ## Tre schermate, uno stato
 *
 * Vetrina → scheda → riproduzione, e si torna indietro con un tasto. Non sono tre booleani: sono un
 * valore solo, perché «scheda aperta» e «film in riproduzione» insieme non significano niente e il
 * codice non deve ricordarsi di non produrlo. È la stessa correzione fatta a `section` dentro
 * `LiveHost`.
 */

interface VodHostProps {
  readonly composition: Composition;
  readonly cache: CatalogCache;
  readonly credentials: Credentials;
  readonly kind: VodKind;
  /**
   * Passa all'altro catalogo.
   *
   * Serve solo al telefono, dove Film e Serie sono due etichette in cima alla stessa vetrina invece
   * di due voci di menu: la sezione la tiene chi ci sta sopra, quindi il cambio si chiede a lui.
   */
  readonly onPickKind?: (kind: VodKind) => void;
  readonly serverNowMs: number;
  /** Indietro dalla vetrina: chiede il menu. */
  readonly onRequestMenu: () => void;
  readonly menuHasFocus: boolean;
  /** Vero mentre un titolo è a schermo pieno: il guscio nasconde la colonna del menu. */
  readonly onWatchingChange: (watching: boolean) => void;
  /**
   * Il titolo su cui aprirsi, invece della vetrina.
   *
   * Ci arriva la ricerca: chi ha cercato «Matrix» e ha premuto OK vuole **la scheda di Matrix**, non
   * la vetrina dei film con Matrix da ritrovare. Si legge solo al montaggio — è uno stato iniziale,
   * non un comando: cambiandolo mentre la sezione è aperta si sposterebbe la schermata sotto le mani
   * di chi la sta usando.
   */
  readonly initialItemId?: string | null;
  /**
   * Il PIN archiviato, o `null` quando non ce n'è uno.
   *
   * Arriva da chi sta sopra e non si rilegge qui: lo leggono in tre — le impostazioni, la pagina del
   * PIN e questa schermata — e tre letture dall'archivio per lo stesso dato divergono alla prima
   * modifica fatta da una delle tre.
   */
  readonly storedPin?: string | null;
}

type View =
  | { readonly kind: 'shelf' }
  /** L'elenco completo dei generi: ci si arriva dalla pastiglia «Tutti i generi · N». */
  | { readonly kind: 'genres' }
  | { readonly kind: 'detail'; readonly itemId: string }
  /** `episode` è `null` per un film. `fromStart` ignora l'avanzamento archiviato. */
  | {
      readonly kind: 'watch';
      readonly itemId: string;
      readonly episode: EpisodeInfo | null;
      readonly fromStart: boolean;
    };

export function VodHost({
  composition,
  cache,
  credentials,
  kind,
  onPickKind,
  serverNowMs,
  onRequestMenu,
  menuHasFocus,
  onWatchingChange,
  initialItemId = null,
  storedPin = null,
}: VodHostProps): ReactNode {
  const [items, setItems] = useState<ReadonlyArray<VodItem>>([]);
  const [categories, setCategories] = useState<ReadonlyArray<ChannelCategory>>([]);
  const [progress, setProgress] = useState<WatchProgress>(EMPTY_PROGRESS);
  const [recentIds, setRecentIds] = useState<ReadonlyArray<string>>([]);
  /** «La mia lista», locale come i preferiti. */
  const [myList, setMyList] = useState<ReadonlyArray<string>>([]);
  const [loading, setLoading] = useState(true);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [view, setView] = useState<View>(
    initialItemId != null && initialItemId !== ''
      ? { kind: 'detail', itemId: initialItemId }
      : { kind: 'shelf' },
  );
  const [detail, setDetail] = useState<SeriesDetail | null>(null);
  /** La scheda di un film: trama, voto, durata, immagine larga. `null` per una serie. */
  const [movieDetail, setMovieDetail] = useState<MovieDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  /**
   * Perché la scheda non è arrivata, o `null`.
   *
   * L'errore veniva **inghiottito**: `catch` metteva `detail` a `null` e finiva lì, quindi una serie
   * senza stagioni e un gateway che non risponde erano lo stesso schermo. Il 30-09-2026 il server di
   * sviluppo girava senza `PLAYER_GATEWAY`, quindi `player_api.php` restituiva `index.html` con 200 e
   * ogni scheda falliva — mentre le vetrine, che da quando il catalogo non scade più leggono
   * dall'archivio locale, continuavano a disegnarsi. L'app sembrava viva e taceva.
   */
  const [detailErrorCode, setDetailErrorCode] = useState<ErrorCode | string | null>(null);

  const gateway = useMemo(() => composition.createGateway(), [composition]);
  const translator = useTranslator();
  const phone = usePhoneLayout();

  /*
    Il catalogo, le categorie, l'avanzamento e i recenti: **una volta per tipo**.

    Tutto dall'archivio locale quando c'è, e dal gateway solo a scadenza. `revalidate` è falso perché
    questa non è l'apertura dell'app: chi vuole forzare ha il tasto «Aggiorna catalogo» nella colonna.
  */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrorCode(null);
    void (async () => {
      try {
        const [catalog, categoryList, stored, recent, saved] = await Promise.all([
          cache.vod(credentials, kind, false),
          // L'elenco intero e non la mappa dei nomi: da qui si costruisce l'**albero**, e una mappa
          // `id → nome` butta via il padre — cioè tutte le sottocategorie.
          cache.vodCategoryList(credentials, kind, false),
          cache.progress(),
          cache.recentVod(),
          cache.myList(),
        ]);
        if (cancelled) return;
        setItems(catalog.items);
        setCategories(categoryList);
        setProgress(stored);
        setRecentIds(recent);
        setMyList(saved);
      } catch (error) {
        if (cancelled) return;
        // Il codice si mostra: una vetrina vuota sarebbe indistinguibile da un catalogo senza film,
        // cioè da un problema di abbonamento.
        const detail = thrownDetail(error, Date.now());
        setErrorCode(detail.code);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cache, credentials, kind]);

  const byId = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);

  /** I nomi, che è quel che serve a chi disegna una fascia. L'albero è qui sotto. */
  const categoryNames = useMemo(
    () => new Map(categories.map((category) => [category.id, category.name])),
    [categories],
  );

  /**
   * L'albero delle categorie, con i conteggi.
   *
   * I conteggi si fanno **sui titoli che questa linea ha davvero**, non su quelli che la categoria
   * dichiara: un numero che comprende ciò che non si può guardare è un numero che mente a chi legge.
   */
  const categoryTree = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of items) {
      if (item.categoryId === '') continue;
      counts.set(item.categoryId, (counts.get(item.categoryId) ?? 0) + 1);
    }
    return buildCategoryTree(categories, counts);
  }, [categories, items]);

  /*
    Il **percorso** aperto nell'albero dei generi, quando ce n'è uno: `['azione', 'spionaggio']`.

    Era un genere solo — `{id, name}` — e non poteva essere altro, perché le categorie arrivavano
    piatte. Adesso sono un albero di profondità libera, e quel che si sta guardando è un cammino:
    serve per il percorso in cima, e serve perché Indietro risalga di **un** livello invece di
    uscire.

    Sta qui e non dentro la schermata perché sopravvive all'andata e ritorno su una scheda: si apre
    «Thriller», si entra in un film, si torna indietro, e si è ancora in «Thriller». Tenendolo nella
    schermata si perderebbe a ogni smontaggio, cioè ogni volta che si guarda qualcosa.
  */
  const [genrePath, setGenrePath] = useState<ReadonlyArray<string>>([]);
  const [genreSort, setGenreSort] = useState<GenreSort>('newest');
  const [unwatchedOnly, setUnwatchedOnly] = useState(false);

  /*
    La **lettera** su cui la vetrina è ridotta, che non è un nodo dell'albero.

    Un catalogo senza categorie nominate si raggruppa per iniziale — ed è successo davvero: film e
    serie non hanno mai avuto una categoria su nessuna installazione finché non si è rifatto
    l'import. «A» è una lettera, non un genere: non ha un padre, non ha figli, e la pagina del genere
    non saprebbe cosa disegnarci. Lì resta la vetrina ridotta a una fascia, che per ventisei gruppi
    va benissimo.
  */
  const [letterFilter, setLetterFilter] = useState<{ id: string; name: string } | null>(null);

  const shelf = useMemo(() => {
    if (letterFilter !== null) {
      return buildGenreShelf({
        items,
        kind,
        progress,
        genreId: letterFilter.id,
        genreName: letterFilter.name,
        categoryNames,
      });
    }
    return buildVodShelf({ items, kind, progress, recentIds, categoryNames });
  }, [items, kind, progress, recentIds, categoryNames, letterFilter]);

  /**
   * L'elenco per «Tutti i generi»: **solo le radici**, con il conto dell'albero.
   *
   * Due differenze da `shelf.genres`, e vengono entrambe dall'albero:
   *
   *  - i nomi delle sottocategorie **non compaiono** al primo livello. «Spionaggio» si raggiunge da
   *    «Azione», e vederlo anche lì accanto farebbe sembrare che siano due generi diversi con gli
   *    stessi film;
   *  - il conteggio comprende i discendenti, altrimenti «Azione» dichiarerebbe solo i film che non
   *    stanno in nessuna sua sottocategoria — cioè sembrerebbe più povera di quanto è.
   *
   * Quando non c'è un albero — catalogo raggruppato per iniziale — resta l'elenco della vetrina,
   * che lì è l'alfabeto.
   */
  const allGenres = useMemo(() => {
    if (categoryTree.length === 0) return shelf.genres;
    return rootsAlphabetically(categoryTree).map((node) => ({
      id: node.id,
      name: node.name,
      count: node.count,
      subcategoryCount: node.children.length,
    }));
  }, [categoryTree, shelf.genres]);

  const genre = useMemo(
    () =>
      genrePage({
        roots: categoryTree,
        path: genrePath,
        items,
        progress,
        sort: genreSort,
        unwatchedOnly,
        sectionName: translator.t(kind === 'movie' ? 'vod.movies.title' : 'vod.series.title'),
      }),
    [categoryTree, genrePath, items, progress, genreSort, unwatchedOnly, kind, translator],
  );

  /*
    La scheda di una serie si chiede **quando si apre**, non prima.

    Una chiamata per serie: farla per ogni piastrella di una fascia sarebbe cinquanta richieste per
    disegnare uno schermo, sullo stesso gateway che conta le richieste per indirizzo e che a fine
    settembre ha bandito venti volte in una sera un indirizzo di casa.
  */
  const openItemId = view.kind === 'detail' || view.kind === 'watch' ? view.itemId : null;
  useEffect(() => {
    if (openItemId === null) {
      setDetail(null);
      setMovieDetail(null);
      setDetailErrorCode(null);
      return undefined;
    }
    const item = byId.get(openItemId);
    if (!item) {
      setDetail(null);
      setMovieDetail(null);
      setDetailErrorCode(null);
      return undefined;
    }

    let cancelled = false;
    setLoadingDetail(true);
    setDetailErrorCode(null);

    /*
      Un film ha la **sua** chiamata, e nessuno la faceva.

      `get_vod_info` porta trama, voto TMDB, durata, frase di lancio e immagine larga: tutto quel che
      c'è in tabella e per cui una scheda esiste. La scheda di un film mostrava solo titolo, genere e
      anno — cioè i tre campi del listino — e sembrava un film di cui non si sa niente.
    */
    if (item.kind === 'movie') {
      setDetail(null);
      void cache
        .movieDetail(credentials, openItemId)
        .then((arrived) => {
          if (!cancelled) setMovieDetail(arrived);
        })
        .catch((error: unknown) => {
          // Come per le serie: la scheda che non arriva **non** chiude la schermata. Restano titolo,
          // genere, anno e locandina, e si può guardare il film lo stesso — ma il perché si registra,
          // perché è l'unico posto da cui può arrivare a chi guarda.
          if (cancelled) return;
          setMovieDetail(null);
          setDetailErrorCode(codeOf(error));
        })
        .finally(() => {
          if (!cancelled) setLoadingDetail(false);
        });
      return () => {
        cancelled = true;
      };
    }

    setMovieDetail(null);
    void cache
      .seriesDetail(credentials, openItemId)
      .then((arrived) => {
        if (!cancelled) setDetail(arrived);
      })
      .catch((error: unknown) => {
        /*
          La scheda che non arriva **non** chiude la schermata.

          Restano titolo, genere, anno e locandina — quel che il listino sa — e mancano le stagioni: si
          vede che c'è un problema con gli episodi, non con l'abbonamento. Chiudere e tornare alla
          vetrina farebbe sembrare il titolo inesistente.

          Ma il codice si conserva e si mostra. Senza, «non sono arrivate le stagioni» e «questa serie
          non ha episodi in catalogo» erano lo stesso schermo vuoto.
        */
        if (cancelled) return;
        setDetail(null);
        setDetailErrorCode(codeOf(error));
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cache, credentials, openItemId, byId]);

  useEffect(() => {
    onWatchingChange(view.kind === 'watch');
  }, [view.kind, onWatchingChange]);

  /** Archivia un avanzamento e lo rimette nello stato: la vetrina si riordina da sé. */
  const saveProgress = useCallback(
    (next: WatchProgress) => {
      setProgress(next);
      void cache.saveProgress(next);
    },
    [cache],
  );

  const remember = useCallback(
    (contentId: string) => {
      void cache.rememberVod(contentId).then(setRecentIds);
    },
    [cache],
  );

  const open = useCallback((contentId: string) => setView({ kind: 'detail', itemId: contentId }), []);

  /*
    Le fasce, nella forma che vuole la vetrina del telefono.

    I titoli li compone `rowTitle`, **la stessa funzione del televisore**: il nome di una fascia di
    genere arriva dal catalogo dell'operatore e non si traduce, le altre tre hanno una chiave i18n.
    Rifarlo qui sarebbe un secondo vocabolario per le stesse righe.
  */
  const phoneRows = useMemo(
    () =>
      shelf.rows.map((row) => ({
        id: row.genreId ?? row.kind,
        title: rowTitle(row, translator),
        items: row.items.map((voce) => voce.item),
      })),
    [shelf.rows, translator],
  );

  /*
    I titoli sbloccati in **questa** sessione: il PIN non si richiede due volte di fila per la stessa
    cosa.

    In memoria e non nell'archivio, di proposito: riaccendendo il televisore la protezione torna
    intera. Un elenco che sopravvive allo spegnimento sarebbe un PIN che si chiede una volta sola
    nella vita dell'apparecchio, cioè quasi mai.
  */
  const [unlocked, setUnlocked] = useState<ReadonlySet<string>>(() => new Set());

  /**
   * Apre un genere, e le due strade sono due cose diverse.
   *
   * Una **categoria** apre la pagina del genere: percorso, sottocategorie, ordinamenti, griglia
   * verticale. Una **lettera** — il raggruppamento di un catalogo senza categorie nominate — riduce
   * la vetrina a una fascia, perché una lettera non ha né padre né figli e quella pagina non saprebbe
   * cosa disegnarci.
   *
   * Un identificativo che non è né l'una né l'altra **non apre niente**: sarebbe una schermata
   * intitolata con un UUID. Non può succedere dalle pastiglie — `vodGenres` scarta già le categorie
   * senza nome — e questa riga è il presidio per le strade che non passano di lì.
   */
  const openGenre = useCallback(
    (genreId: string) => {
      if (categoryTree.some((node) => node.id === genreId)) {
        setLetterFilter(null);
        setGenreSort('newest');
        setUnwatchedOnly(false);
        setGenrePath([genreId]);
        setView({ kind: 'shelf' });
        return;
      }
      if (shelf.grouping !== 'initial') return;
      setGenrePath([]);
      setLetterFilter({ id: genreId, name: genreId });
      setView({ kind: 'shelf' });
    },
    [categoryTree, shelf.grouping],
  );

  const openItem = openItemId === null ? null : byId.get(openItemId) ?? null;

  const similar = useMemo(
    () => (openItem === null ? [] : similarTo(openItem, items, progress)),
    [openItem, items, progress],
  );

  /*
    Il titolo aperto con le stagioni innestate, quando ce ne sono.

    Serve alla scheda e a «segna come visto»: `watchStateOf` conta gli episodi da `episodesPerSeason`,
    che il listino lascia vuoto. Senza, una serie guardata per metà risulterebbe mai iniziata — cioè
    l'app annuncerebbe a chi guarda che il suo avanzamento è andato perso.
  */
  const openItemWithSeasons = useMemo(() => {
    if (openItem === null) return null;
    if (openItem.kind !== 'series' || detail === null) return openItem;
    return withSeasons(openItem, detail);
  }, [openItem, detail]);

  const toggleMyList = useCallback(() => {
    if (openItemId === null) return;
    const next = myList.includes(openItemId)
      ? myList.filter((entry) => entry !== openItemId)
      : [...myList, openItemId];
    setMyList(next);
    // In locale non c'è un «dopo» da aspettare: quel che si vede è quel che è salvato.
    void cache.saveMyList(next);
  }, [openItemId, myList, cache]);

  /**
   * Segna visto o da vedere: un film intero, oppure **una stagione**.
   *
   * `season` è `null` per un film. Segnare l'intera serie da un tasto che dice «Segna S2 come vista»
   * era una promessa e un'altra cosa — e senza modo di tornare indietro, perché il segno non ricorda
   * cosa c'era prima di lui.
   */
  const toggleWatched = useCallback((season: number | null) => {
    if (openItemWithSeasons === null) return;
    const item = openItemWithSeasons;
    if (item.kind === 'movie') {
      const seen = (progress.movies[item.id] ?? 0) >= WATCHED_PERCENT;
      const movies = { ...progress.movies };
      if (seen) delete movies[item.id];
      else movies[item.id] = WATCHED_PERCENT;
      saveProgress({ ...progress, movies });
      return;
    }
    if (detail === null) return;
    /*
      Due cicli e non `flatMap`.

      `Array.prototype.flatMap` è arrivato con Chromium 69, e il runtime di una Tizen 5 è fermo al 63:
      lì questa riga sarebbe un `TypeError` a runtime, cioè un tasto «segna come visto» che non fa
      niente su un intero bersaglio. Il compilatore non l'ha fermata — `lib` è ES2018 e l'errore non è
      arrivato comunque — ed è il motivo per cui il vincolo va rispettato a mano invece di essere dato
      per sorvegliato.
    */
    /*
      Gli episodi **della stagione scelta**, non di tutta la serie.

      Due cicli e non `flatMap`: quello è arrivato con Chromium 69 e il runtime di una Tizen 5 è fermo
      al 63 — lì sarebbe un `TypeError`, cioè un tasto che non fa niente su un intero bersaglio.
    */
    const episodes: EpisodeInfo[] = [];
    for (const entry of detail.seasons) {
      if (season !== null && entry.number !== season) continue;
      for (const episode of entry.episodes) episodes.push(episode);
    }
    if (episodes.length === 0) return;

    const allSeen =
      episodes.length > 0 &&
      episodes.every(
        (episode) =>
          (progress.episodes[item.id]?.[episodeProgressKey(episode.season, episode.episode)] ?? 0) >=
          WATCHED_PERCENT,
      );
    /*
      Si tocca **solo** quel che riguarda questa stagione: le altre restano come sono.

      Prima si sostituiva l'intera mappa della serie, quindi segnare una stagione cancellava
      l'avanzamento di tutte le altre — e non lo diceva nessuno.
    */
    const forSeries = { ...(progress.episodes[item.id] ?? {}) };
    for (const episode of episodes) {
      const key = episodeProgressKey(episode.season, episode.episode);
      if (allSeen) delete forSeries[key];
      else forSeries[key] = WATCHED_PERCENT;
    }
    saveProgress({ ...progress, episodes: { ...progress.episodes, [item.id]: forSeries } });
  }, [openItemWithSeasons, detail, progress, saveProgress]);

  /** L'indirizzo di quel che si sta guardando, ricomposto a ogni cambio: film o episodio. */
  const streamUrl = useMemo(() => {
    if (view.kind !== 'watch') return null;
    const contentId = view.episode?.id ?? view.itemId;
    return gateway.vodStreamUrl(credentials, contentId);
  }, [view, gateway, credentials]);

  const onProgressRef = useRef({ progress, saveProgress, remember });
  useEffect(() => {
    onProgressRef.current = { progress, saveProgress, remember };
  }, [progress, saveProgress, remember]);

  /*
    **Il PIN si chiede qui, prima di riprodurre**, e non prima di aprire la scheda.

    Una scheda non mostra niente che vada protetto — titolo, locandina, trama — e chiedere il PIN per
    leggerla trasforma il controllo genitori in un ostacolo per chi sfoglia. Chi lo sente come un
    ostacolo lo spegne, e a quel punto non protegge più niente.

    Senza un PIN archiviato non si blocca nulla: `isLocked` vuole `pinEnabled`, che qui significa
    «quattro cifre ci sono». È il presidio contro il caso peggiore, contenuto bloccato e nessuna
    chiave.
  */
  if (
    view.kind === 'watch' &&
    openItem !== null &&
    storedPin !== null &&
    !unlocked.has(openItem.id) &&
    isLocked(openItem, { pinEnabled: true })
  ) {
    return (
      <PinPrompt
        focusPort={composition.focusPort}
        title={openItem.title}
        storedPin={storedPin}
        /*
          Sbloccato: la riproduzione parte **da sé**. L'azione era rimasta in attesa, e farla rifare
          dopo aver digitato il PIN giusto sarebbe una pressione chiesta per niente.
        */
        onUnlocked={() => setUnlocked((current) => new Set([...current, openItem.id]))}
        // Annullando si torna alla scheda, non alla vetrina: si è arrivati da lì.
        onCancel={() => setView({ kind: 'detail', itemId: openItem.id })}
      />
    );
  }

  if (view.kind === 'watch' && openItem !== null && streamUrl !== null) {
    const episode = view.episode;
    const stored =
      episode === null
        ? progress.movies[openItem.id] ?? 0
        : progress.episodes[openItem.id]?.[episodeProgressKey(episode.season, episode.episode)] ?? 0;
    return (
      <VodPlayerScreen
        focusPort={composition.focusPort}
        createPlayer={composition.createPlayer}
        streamUrl={streamUrl}
        /*
          Titolo e riga sopra, nella forma del disegno (`plVod`): «Film · Thriller» sopra «Il
          testimone», e per un episodio il titolo porta con sé **quale** — «Linea d'ombra · S2 E3».

          Senza il numero nel titolo, due episodi della stessa serie sullo schermo sono
          indistinguibili: la riga grande direbbe la stessa cosa per tutta la stagione.
        */
        title={
          episode === null
            ? openItem.title
            : `${openItem.title} · S${episode.season} E${episode.episode}`
        }
        kicker={[
          translator.t(openItem.kind === 'movie' ? 'vod.kind.movie' : 'vod.kind.series'),
          (detail ?? movieDetail)?.genre !== undefined && (detail ?? movieDetail)!.genre !== ''
            ? (detail ?? movieDetail)!.genre
            : openItem.genre,
        ]
          .filter((part) => part !== '')
          .join(' · ')}
        watched={stored >= WATCHED_PERCENT}
        /*
          Dal lettore si segna **quello che si sta guardando**, non la serie intera.

          `markCurrent()` nel prototipo fa esattamente questo. Segnare tutta la serie da qui sarebbe
          un tasto che, premuto per sbaglio su una puntata, cancella l'avanzamento di quaranta — e
          senza modo di tornare indietro, perché il segno non ricorda cosa c'era prima.
        */
        onToggleWatched={() => {
          const current = onProgressRef.current;
          const next =
            episode === null
              ? markMovieWatched(current.progress, openItem.id, stored < WATCHED_PERCENT)
              : markEpisodeWatched(
                  current.progress,
                  openItem.id,
                  episode,
                  stored < WATCHED_PERCENT,
                );
          current.saveProgress(next);
        }}
        nextLabel={
          episode !== null && detail !== null
            ? nextEpisodeLabel(detail, episode, translator)
            : null
        }
        /*
          La card dell'episodio successivo, che compare negli ultimi tre minuti.

          `null` per un film e per l'ultimo episodio dell'ultima stagione: **quando** mostrarla lo
          decide il lettore, che è l'unico che sa a che punto è; *quale* sia il successivo lo decide
          il dominio, che è l'unico che ha le stagioni.
        */
        next={
          episode !== null && detail !== null
            ? (() => {
                const upcoming = nextEpisodeOf(detail, episode);
                if (upcoming === null) return null;
                return {
                  label: `S${upcoming.episode.season} E${upcoming.episode.episode} · ${upcoming.episode.title}`,
                  startsSeason: upcoming.startsSeason,
                  season: upcoming.episode.season,
                };
              })()
            : null
        }
        onPlayNext={() => {
          if (episode === null || detail === null) return;
          const upcoming = nextEpisodeOf(detail, episode);
          if (upcoming === null) return;
          // **Dall'inizio**: si sta cominciando una puntata nuova, e riprenderla da dove la si era
          // lasciata è un caso che qui non esiste — se la si fosse già vista non la si proporrebbe.
          setView({ kind: 'watch', itemId: openItem.id, episode: upcoming.episode, fromStart: true });
        }}
        /*
          Si riprende solo se non si è chiesto «dall'inizio» e se non è già finito.

          Ripartire dal 100% di un film visto significherebbe aprirlo sui titoli di coda: chi lo
          riapre da capo lo sta riguardando, ed è la cosa che chiede premendo il tasto principale.
        */
        resumePercent={!view.fromStart && stored > 0 && stored < WATCHED_PERCENT ? stored : null}
        onProgress={(percent) => {
          const current = onProgressRef.current;
          const next =
            episode === null
              ? recordMovieProgress(current.progress, openItem.id, percent)
              : recordEpisodeProgress(
                  current.progress,
                  openItem.id,
                  episode.season,
                  episode.episode,
                  percent,
                );
          if (next !== current.progress) {
            current.saveProgress(next);
            current.remember(openItem.id);
          }
        }}
        onEnded={() => setView({ kind: 'detail', itemId: openItem.id })}
        onExit={() => setView({ kind: 'detail', itemId: openItem.id })}
        onError={() => {
          /*
            L'errore lo mostra la schermata del lettore, che è dove si sta guardando.

            **Non** si torna alla scheda da soli: un film che non parte e una schermata che si chiude da
            sé sono indistinguibili da un tasto che non ha funzionato, e chi guarda lo ripreme.
          */
        }}
      />
    );
  }

  if (view.kind === 'genres') {
    /*
      L'elenco dei generi sul telefono ha un campo per filtrare, che sul televisore non ci sarebbe:
      col telecomando scrivere è costoso, col dito è il modo più veloce di arrivare a «Thriller» in
      un elenco di quaranta voci.
    */
    if (phone) {
      return (
        <PhoneGenresScreen
          genres={allGenres}
          onPick={(genreId) => {
            openGenre(genreId);
            setView({ kind: 'shelf' });
          }}
        />
      );
    }

    return (
      <VodGenresScreen
        focusPort={composition.focusPort}
        kind={kind}
        yours={shelf.chips}
        all={allGenres}
        onChoose={openGenre}
        onExit={() => setView({ kind: 'shelf' })}
      />
    );
  }

  if (view.kind === 'detail' && openItemWithSeasons !== null) {
    /*
      La scheda del telefono. **Non** è quella del televisore resa elastica: là il titolo è in
      caratteri da oltre cento punti accanto a una colonna di dati, e in una colonna da 360 punti
      scendeva una parola per riga, con «Film · 2025» impilato in verticale.

      Le azioni sono le stesse, con gli stessi dati: cambia il disegno, non cosa fa un tasto.
    */
    if (phone) {
      return (
        <PhoneVodDetailScreen
          item={openItemWithSeasons}
          detail={detail}
          movie={movieDetail}
          progress={progress}
          loadingDetail={loadingDetail}
          detailErrorCode={detailErrorCode}
          onPlayMovie={(fromStart) =>
            setView({ kind: 'watch', itemId: openItemWithSeasons.id, episode: null, fromStart })
          }
          onPlayEpisode={(episode, fromStart) =>
            setView({ kind: 'watch', itemId: openItemWithSeasons.id, episode, fromStart })
          }
          onToggleMyList={toggleMyList}
          inMyList={openItemId !== null && myList.includes(openItemId)}
          onExit={() => setView({ kind: 'shelf' })}
        />
      );
    }

    return (
      <VodDetailScreen
        focusPort={composition.focusPort}
        item={openItemWithSeasons}
        detail={detail}
        movie={movieDetail}
        progress={progress}
        similar={similar}
        loadingDetail={loadingDetail}
        detailErrorCode={detailErrorCode}
        onPlayMovie={(fromStart) =>
          setView({ kind: 'watch', itemId: openItemWithSeasons.id, episode: null, fromStart })
        }
        onPlayEpisode={(episode, fromStart) =>
          setView({ kind: 'watch', itemId: openItemWithSeasons.id, episode, fromStart })
        }
        onToggleWatched={toggleWatched}
        onToggleMyList={toggleMyList}
        inMyList={openItemId !== null && myList.includes(openItemId)}
        onOpen={open}
        onExit={() => setView({ kind: 'shelf' })}
      />
    );
  }

  /*
    La pagina di un genere: **una schermata a sé**, non la vetrina ridotta.

    Una categoria da duecento titoli in una fascia orizzontale è un corridoio — ottanta pressioni per
    arrivare in fondo, e mai un'idea di quanti siano. Qui la griglia scorre in verticale e il
    conteggio è la prima cosa che si legge.
  */
  if (genrePath.length > 0) {
    /*
      La pagina di un genere sul telefono. Quella del televisore apre con un'intestazione alta —
      briciole, nome in caratteri da oltre settanta punti, conteggio e tre file di comandi — che su
      uno schermo stretto occupa **tutto** lo schermo: si apre un genere per vedere cosa c'è dentro,
      e non si vede niente senza scorrere.

      Le azioni sono le stesse, con gli stessi dati. Le briciole no: su un telefono si torna col
      tasto Indietro, e una fila di briciole su una riga stretta è un'altra riga che non ci sta.
    */
    if (phone) {
      return (
        <PhoneGenrePageScreen
          page={genre}
          sort={genreSort}
          unwatchedOnly={unwatchedOnly}
          onSort={setGenreSort}
          onToggleUnwatched={() => setUnwatchedOnly((on) => !on)}
          onOpenChild={(categoryId) => {
            // Scendendo, ordinamento e filtro si azzerano: sono scelte fatte su **quell'elenco**.
            setGenreSort('newest');
            setUnwatchedOnly(false);
            setGenrePath((path) => [...path, categoryId]);
          }}
          onOpen={open}
          onBack={() => {
            // Un livello per volta: dalla radice si torna alla vetrina.
            setGenreSort('newest');
            setUnwatchedOnly(false);
            setGenrePath((path) => path.slice(0, -1));
          }}
        />
      );
    }

    return (
      <VodGenreScreen
        focusPort={composition.focusPort}
        kind={kind}
        page={genre}
        sort={genreSort}
        unwatchedOnly={unwatchedOnly}
        onSort={setGenreSort}
        onToggleUnwatched={() => setUnwatchedOnly((on) => !on)}
        onOpenChild={(categoryId) => {
          // Scendendo, ordinamento e filtro si azzerano: sono scelte fatte su **quell'elenco**, e
          // portarle in un elenco diverso è un filtro che chi guarda non ricorda di aver acceso.
          setGenreSort('newest');
          setUnwatchedOnly(false);
          setGenrePath((path) => [...path, categoryId]);
        }}
        onOpenPath={(path) => {
          setGenreSort('newest');
          setUnwatchedOnly(false);
          setGenrePath(path);
        }}
        onOpen={open}
        /*
          Indietro **risale di un livello**, e solo dal primo esce.

          È la differenza fra scendere in «Azione › Spionaggio › Guerra fredda» e non poterne più
          uscire se non ricominciando da capo: il tasto che riporta indietro deve riportare indietro
          di un passo, non di tutti.
        */
        onBack={() => {
          setGenreSort('newest');
          setUnwatchedOnly(false);
          setGenrePath((path) => path.slice(0, -1));
        }}
        onRequestMenu={onRequestMenu}
        menuHasFocus={menuHasFocus}
      />
    );
  }

  if (phone) {
    /*
      La vetrina del telefono: due etichette in cima e le fasce sotto.

      Si monta **solo sulla vetrina**: la scheda di un titolo e il lettore restano quelli del
      televisore, che a schermo pieno vanno bene anche qui. Sostituirli adesso vorrebbe dire rifare
      stagioni, episodi e ripresa dell'avanzamento — cioè la parte che oggi funziona.
    */
    return (
      <PhoneVodScreen
        kind={kind === 'movie' ? 'movies' : 'series'}
        onPickKind={(quale) => onPickKind?.(quale === 'movies' ? 'movie' : 'series')}
        rows={phoneRows}
        onOpenItem={open}
        onOpenGenres={() => setView({ kind: 'genres' })}
      />
    );
  }

  return (
    <VodScreen
      focusPort={composition.focusPort}
      kind={kind}
      shelf={shelf}
      serverNowMs={serverNowMs}
      onOpen={open}
      /*
        La **lettera** su cui la vetrina è ridotta, per un catalogo senza categorie nominate. Le
        categorie vere passano invece dalla pagina del genere, qui sopra: una lettera non ha né padre
        né figli, e quella pagina non saprebbe cosa disegnarci.
      */
      genreFilter={letterFilter}
      onOpenGenre={openGenre}
      onOpenAllGenres={() => setView({ kind: 'genres' })}
      onClearGenre={() => setLetterFilter(null)}
      onRequestMenu={onRequestMenu}
      menuHasFocus={menuHasFocus}
      loading={loading}
      errorCode={errorCode}
    />
  );
}

/**
 * Il codice d'errore di qualunque cosa sia stata lanciata.
 *
 * Un `PlayerError` lo porta già; qualunque altra cosa — un `TypeError` nel nostro codice, un guasto
 * della rete che il livello sotto non ha tradotto — passa da `parseGatewayError`, che ha il compito di
 * dare un codice anche a ciò che non ne ha. Serve perché la schermata deve poter dire **qualcosa**: un
 * ramo che restituisce `null` per il caso imprevisto rimetterebbe lo schermo vuoto proprio quando il
 * guasto è di un tipo che non avevamo previsto, cioè quando saperlo conta di più.
 */
function codeOf(error: unknown): ErrorCode | string {
  return thrownDetail(error, Date.now()).code;
}

/** Segna un film come visto, o toglie il segno. Togliere **cancella**: non c'è un «visto a metà». */
function markMovieWatched(
  progress: WatchProgress,
  movieId: string,
  watched: boolean,
): WatchProgress {
  const movies = { ...progress.movies };
  if (watched) movies[movieId] = WATCHED_PERCENT;
  else delete movies[movieId];
  return { ...progress, movies };
}

/** Lo stesso per un episodio, senza toccare gli altri della serie. */
function markEpisodeWatched(
  progress: WatchProgress,
  seriesId: string,
  episode: EpisodeInfo,
  watched: boolean,
): WatchProgress {
  const forSeries = { ...(progress.episodes[seriesId] ?? {}) };
  const key = episodeProgressKey(episode.season, episode.episode);
  if (watched) forSeries[key] = WATCHED_PERCENT;
  else delete forSeries[key];
  return { ...progress, episodes: { ...progress.episodes, [seriesId]: forSeries } };
}

/**
 * «Prossimo: S2 E4», o `null` alla fine della serie.
 *
 * Attraversa il confine fra stagioni, perché `episodeAfter` lo fa: l'ultimo episodio di una stagione
 * non è la fine di niente, e scriverci «fine» sarebbe sbagliato proprio nel momento in cui chi guarda
 * decide se continuare.
 */
function nextEpisodeLabel(
  detail: SeriesDetail,
  current: EpisodeInfo,
  translator: ReturnType<typeof useTranslator>,
): string | null {
  const next = episodeAfter(detail, current);
  if (next === null) return null;
  return translator.t('vod.player.next', { season: next.season, episode: next.episode });
}
